import Vue from 'vue';
import { createDecorator } from 'vue-class-component';

const noop = () => undefined;
const dragEvents = [
    'dragenter',
    'dragleave',
    'drop',
];

export interface FileStructure {
    files: File[];
    allFiles: File[];
    directories: Array<{ name: string; children: FileStructure }>;
}
async function processFilesystemEntries(entries: any[]): Promise<FileStructure> {
    const fileEntries = entries.filter(entry => entry.isFile);
    const dirEntries = entries.filter(entry => entry.isDirectory);
    const structure = {
        files: await Promise.all(
            fileEntries.map(fileEntry => new Promise(
                (resolve, reject) => {
                    fileEntry.file(resolve, reject);
                }
            )) as Array<Promise<File>>
        ),
        directories: await Promise.all(
            dirEntries.map(async dirEntry => {
                let entries: any[] = [];
                const reader = dirEntry.createReader();
                let currentEntries: any[] = [];
                do {
                    currentEntries = await new Promise((resolve, reject) => reader.readEntries(resolve));
                    entries = [...entries, ...currentEntries];
                } while (currentEntries.length);

                return {
                    name: dirEntry.name,
                    children: await processFilesystemEntries(entries)
                };
            })
        )
    };
    
    const childFiles = structure.directories.map(({ children: { files } }) => files).flat();

    return {
        ...structure,
        allFiles: [ 
            ...structure.files, 
            ...childFiles
        ]
    };
}

function hasFiles(e: DragEvent) {
    return !!e.dataTransfer && (
        Array.from(e.dataTransfer.items).some(item => item.kind === 'file') ||
        e.dataTransfer.files.length > 0
    );
}

function trackDrag(target: { 
    addEventListener(type: string, listener: EventListenerOrEventListenerObject, options?: boolean | AddEventListenerOptions): void;
    removeEventListener(type: string, listener: EventListenerOrEventListenerObject, options?: boolean | EventListenerOptions): void;
}, enterListener: (items: DataTransferItemList) => void, leaveListener: (items: DataTransferItemList) => void) {
    let dragCounter = 0;
    const dragListener = (e: Event) => {
        e.preventDefault();
        if (
            !hasFiles(e as DragEvent)
        ) {
            return;
        }

        if (e.type === 'dragenter') {
            if (dragCounter === 0) {
                enterListener((e as DragEvent).dataTransfer!.items);
            }

            dragCounter++;
        } else if (e.type === 'dragleave') {
            dragCounter--;
            if (dragCounter === 0) {
                leaveListener((e as DragEvent).dataTransfer!.items);
            }
        } else if (e.type === 'drop') {
            dragCounter = 0;
            leaveListener((e as DragEvent).dataTransfer!.items);
        }
    };
    dragEvents.forEach(eventName => target.addEventListener(eventName, dragListener));

    target.addEventListener('dragover', e => {
        // preventDefault necessary to prevent drop redirecting to file://
        e.preventDefault();
    });

    return () => dragEvents.forEach(eventName => target.removeEventListener(eventName, dragListener));    
} 

const dragDropBus = new Vue();
trackDrag(document, (items: DataTransferItemList) => dragDropBus.$emit('globalDragstart', items), (items: DataTransferItemList) => dragDropBus.$emit('globalDragend', items));

export default createDecorator((options) => {
    const originalMounted = options.mounted || noop;
    const originalBeforeDestroy = options.beforeDestroy || noop;

    options.mounted = function<C extends Vue>(this: C) {
        const removeEvents = trackDrag(
            this.$el,
            (items: DataTransferItemList) => {
                this.$emit('dragenter', items);
            },
            (items: DataTransferItemList) => {
                this.$emit('dragleave', items);
            }
        );
        const enterListener = (items: DataTransferItemList) => {
            this.$emit('globalDragstart', items);
        };
        const leaveListener = (items: DataTransferItemList) => {
            this.$emit('globalDragend', items);
        };
        const dropListener = async (e: DragEvent) => {
            if (!hasFiles(e)) {
                return;
            }

            const entries = Array.from(e.dataTransfer!.items)
                .map(item => item.webkitGetAsEntry?.())
                .filter(Boolean);
            const structure = await processFilesystemEntries(entries);
            if (!entries.length) {
                structure.files = Array.from(e.dataTransfer!.files);
                structure.allFiles = structure.files;
            }
            this.$emit('drop', structure);
        };
        dragDropBus.$on('globalDragstart', enterListener);
        dragDropBus.$on('globalDragend', leaveListener);
        this.$el.addEventListener('drop', dropListener as any);

        this.$data.___removeDragDrop = () => {
            removeEvents();
            dragDropBus.$off('globalDragstart', enterListener);
            dragDropBus.$off('globalDragend', leaveListener);
            this.$el.removeEventListener('drop', dropListener as any);
        }

        originalMounted.call(this);
    };

    options.beforeDestroy = function<C extends Vue>(this: C) {
        this.$data.___removeDragDrop();
        originalBeforeDestroy.call(this);
    };
});
